import { Vue } from 'vue-property-decorator';
import { CartItemModel, CartModel } from '@/models/CartModel';
import { MerchantDetailsModel } from '@/models/MerchantDetailsModel';
import { MenuItemModel } from '@/models/MenuItemModel';
import { MenuExtraListItemModel } from '@/models/MenuExtraListItemModel';
import ActionTypes from '@/constants/action-types';
import { CartItemExtraView, CartItemView } from '@/types';

export default abstract class Cart extends Vue {
  protected abstract isExpanded: boolean;

  protected get cart(): CartModel | null {
    return this.$store.state.cart.cart;
  }

  protected get merchant(): MerchantDetailsModel {
    return this.$store.state.merchant.merchant;
  }

  protected onEditCartItem(cartItem: CartItemModel): void {
    const menuItem = this.menuItemsMap.get(cartItem.menuItemId);
    this.$emit('editCartItem', cartItem, menuItem, this.merchant.id);
  }

  protected get menuItemsMap(): Map<number, MenuItemModel> {
    const allItems: MenuItemModel[] = this.merchant.menus.map((e) => e.submenus.map((s) => s.items)).flat(2);

    const kvArray: Array<[number, MenuItemModel]> = allItems.map((e) => [e.id, e]);

    return new Map<number, MenuItemModel>(kvArray);
  }

  protected get cartItemViews(): CartItemView[] {
    if (!(this.merchant && this.cart)) {
      return [];
    }

    return this.cart.cartItems.map((cartItem: CartItemModel) => {
      const menuItem = this.menuItemsMap.get(cartItem.menuItemId) as MenuItemModel;
      const size = menuItem.sizes.find((s) => s.id === cartItem.menuItemSizeId)?.name;

      const menuItemExtrasKvArray: Array<[number, MenuExtraListItemModel]> = menuItem.extraLists
        .map((l) => l.itemExtraListItems)
        .flat()
        .map((e) => [e.id, e]);

      const menuItemExtrasMap: Map<number, MenuExtraListItemModel> = new Map<number, MenuExtraListItemModel>(
        menuItemExtrasKvArray,
      );

      const extras: CartItemExtraView[] = cartItem.extras.map((e) => ({
        id: e.id,
        unitPrice: e.unitPrice,
        name: menuItemExtrasMap.get(e.itemExtraListItemId)?.name,
      }));

      return {
        cartItem,
        name: menuItem.name,
        size,
        extras,
      };
    });
  }

  protected onRemoveCartItem(item: CartItemView): void {
    this.$emit('removeItem', item.cartItem, item.name);
  }

  protected async onClearCart(): Promise<void> {
    this.$emit('clear-cart');
    await this.$store.dispatch(`cart/${ActionTypes.EmptyCart}`);
  }
}
